import { normalizeDate } from '../normalizeDate/normalizeDate';
import { DateDisplayFormat, InkDateType, LocaleMapping } from '../../types';
import { LocaleType } from '../../constants/localeConstants';
import { formatDateParts } from './formatDateParts';
import { createUtcStartOfDay } from '../internal/temporalDate/dateBuilder';
import { getResolvedLocale } from '../internal/getResolvedLocales';

type FormatInkDateType = {
  value: InkDateType;
  format?: Extract<DateDisplayFormat, 'condensed' | 'monthCondensed'>;
  locale?: LocaleType | LocaleMapping;
};

type FormattedDateResult = {
  formatted: string;
  isoString?: string;
  isValidDate: boolean;
  inputLocale?: LocaleType;
  outputLocale?: LocaleType;
};

/**
 * Parses and formats a given date value based on the specified format and locale.
 *
 * This function:
 * - Normalizes the input value (Date, Moment, string, etc.)
 * - Returns a formatted string based on the selected output locale
 * - Exposes the resolved `inputLocale` and `outputLocale`, which may differ from the original input if it was invalid
 *
 * `isValidDate` and `isoString` are exposed explicitly to avoid re-computation by consumers.
 *
 * @param {InkDateType} value - The date input to be parsed and formatted.
 * @param {'condensed' | 'monthCondensed'} format - The desired output format:
 *   - `'condensed'`: Short format like "15 Jun 2024"
 *   - `'monthCondensed'`: Month-based format like "Jun 2024"
 * @param {LocaleType | { input: LocaleType; output: LocaleType }} [locale] - The locale used for parsing and formatting.
 *   - If an object is passed, `input` is used for parsing and `output` for formatting.
 *   - If a string is passed, both parsing and formatting will use the same locale.
 *
 *  @returns {{
 *   formatted: string;
 *   isoString?: string;
 *   isValidDate: boolean;
 *   inputLocale: LocaleType;
 *   outputLocale: LocaleType;
 * }} An object containing:
 *   - `formatted`: the final formatted date string or the original value if invalid
 *   - `isoString`: ISO string format (if valid)
 *   - `isValidDate`: whether the input was successfully normalized
 *   - `inputLocale`: the final locale used to parse the input
 *   - `outputLocale`: the final locale used to format the output
 */
export const formatInkDate = ({ value, format = 'condensed', locale }: FormatInkDateType): FormattedDateResult => {
  const { input: validInputLocale, output: validOutputLocale } = getResolvedLocale(locale);
  const { dateParts, isValidDate } = normalizeDate({ value, locale: validInputLocale });
  if (!dateParts)
    return {
      formatted: value.toString(),
      isValidDate: false,
      inputLocale: validInputLocale,
      outputLocale: validOutputLocale,
    };
  // `isValidDate` and `isoString` could technically be derived from normalizeDate and formatDateParts,
  // but exposing them explicitly makes this function more ergonomic for consumers.
  // It avoids requiring re-parsing or re-formatting down the chain.
  return {
    isValidDate,
    formatted: isValidDate ? formatDateParts(dateParts, format, validOutputLocale) : value?.toString(),
    isoString: createUtcStartOfDay(dateParts)?.toISOString(),
    inputLocale: validInputLocale,
    outputLocale: validOutputLocale,
  };
};
